/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IContentBlockAdditionalContentItemViewProps,
    IContentBlockAdditionalContentViewProps,
    IContentBlockViewProps,
    ILinksData
} from '@msdyn365-commerce-modules/content-block';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

interface IExtendedContentBlockConfig {
    ctaLinks?: ILinksData[];
    imageGradient?: boolean;
}

interface IExtendedContentBlockViewProps extends IContentBlockViewProps {
    config: IContentBlockViewProps['config'] & IExtendedContentBlockConfig;
}

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: IContentBlockAdditionalContentItemViewProps) => {
                return (
                    <>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </>
                );
            })}
        </Node>
    );
};

/**
 * Render CTA Links.
 * @param ctaLinks - Array of CTA links.
 * @returns JSX Element.
 */
const renderCtaLinks = (ctaLinks: ILinksData[]) => {
    return (
        <div className='ms-content-block__cta-links'>
            {ctaLinks.map((link, index) => (
                <a
                    key={index}
                    className='ms-content-block__cta-link-item'
                    href={link.linkUrl.destinationUrl}
                    target={link.openInNewTab ? '_blank' : undefined}
                >
                    {link.linkText}
                </a>
            ))}
        </div>
    );
};

/**
 * Render View.
 * @param props - The view props.
 * @returns -The JSX Element.
 */
const contentBlockView: React.FC<IExtendedContentBlockViewProps> = props => {
    const {
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        additionalContent,
        config
    } = props;

    const { ctaLinks, imageGradient } = config;

    // @ts-expect-error HTML element need to be clear on run time.
    const hasImage: boolean = image.props.src;
    const imageClass: string = hasImage ? `${detailsContainer.className} withImage` : `${detailsContainer.className} withoutImage`;

    if (imageLink) {
        return (
            <Module {...contentBlockContainer}>
                <Node
                    {...contentBlockAnchorTag}
                    href={imageLink}
                    className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                    aria-label={imageAriaLabel}
                >
                    <Node {...imageContainer}>{image}</Node>
                </Node>
                <Node {...detailsContainer}>
                    {title}
                    {text}
                    {links}
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            </Module>
        );
    }
    return (
        <Module {...contentBlockContainer}>
            <Node {...imageContainer} className={classnames(imageContainer.className, imageGradient && 'ms-content-block__image-gradient')}>
                {image}
            </Node>
            <Node className={imageClass}>
                {title}
                {text}
                {links}
                {additionalContent && renderAdditionalContent(additionalContent)}
                {ctaLinks && renderCtaLinks(ctaLinks)}
            </Node>
        </Module>
    );
};

export default contentBlockView;
